import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  HelpCircle, Book, Video, MessageCircle, Mail,
  ChevronDown, ChevronRight, Search, ExternalLink,
  Zap, Users, Clock, Shield, Globe, Smartphone
} from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const Help: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const categories = [
    { id: 'all', name: 'Tutte', icon: Book },
    { id: 'start', name: 'Iniziare', icon: Zap },
    { id: 'automation', name: 'Automazioni', icon: Clock },
    { id: 'groups', name: 'Gruppi', icon: Users },
    { id: 'security', name: 'Sicurezza', icon: Shield },
  ];

  const faqs: FAQItem[] = [
    {
      question: 'Come creo la mia prima automazione?',
      answer: 'Per creare la tua prima automazione, vai nell\'editor e trascina un nodo Trigger dalla sidebar. Poi aggiungi le azioni che vuoi eseguire collegandole con i punti di connessione. Infine, salva la tua automazione con un nome descrittivo.',
      category: 'start'
    },
    {
      question: 'Posso programmare l\'invio di messaggi?',
      answer: 'Sì! Usa il trigger "Programmato" e configura l\'espressione cron per definire quando inviare i messaggi. Puoi programmare invii giornalieri, settimanali o con qualsiasi frequenza desideri.',
      category: 'automation'
    },
    {
      question: 'Come gestisco i gruppi WhatsApp?',
      answer: 'Puoi creare automazioni specifiche per i gruppi usando i trigger "Messaggio Gruppo", "Ingresso Gruppo" e "Uscita Gruppo". Le azioni includono l\'invio di messaggi ai gruppi, l\'aggiunta/rimozione di membri e la promozione ad admin.',
      category: 'groups'
    },
    {
      question: 'Le mie automazioni sono sicure?',
      answer: 'Assolutamente sì. Tutti i dati sono criptati e salvati localmente nel tuo browser. Non condividiamo mai le tue informazioni con terze parti.',
      category: 'security'
    },
    {
      question: 'Cosa sono le condizioni?',
      answer: 'Le condizioni ti permettono di creare logiche if/then nelle tue automazioni. Ad esempio, puoi inviare messaggi diversi in base al contenuto del messaggio ricevuto o all\'orario.',
      category: 'automation'
    },
    {
      question: 'Come uso i filtri?',
      answer: 'I filtri ti permettono di limitare l\'esecuzione delle automazioni solo a determinati utenti o tipi di messaggi. Puoi filtrare per tipo utente, lingua, posizione e altro.',
      category: 'automation'
    },
    {
      question: 'Posso testare le mie automazioni?',
      answer: 'Sì! Usa il pulsante "Test" nella toolbar per simulare l\'esecuzione del tuo flow. Vedrai un\'animazione che mostra come i messaggi fluiscono attraverso i nodi.',
      category: 'start'
    },
    {
      question: 'Come esporto/importo le automazioni?',
      answer: 'Usa i pulsanti di esportazione/importazione nella toolbar. Le automazioni vengono salvate come file JSON che puoi condividere o fare backup.',
      category: 'automation'
    },
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const resources = [
    {
      title: 'Video Tutorial',
      description: 'Guarda i nostri video tutorial per imparare velocemente',
      icon: Video,
      link: '#',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Documentazione',
      description: 'Leggi la documentazione completa con esempi pratici',
      icon: Book,
      link: '#',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      title: 'Community',
      description: 'Unisciti alla community e chiedi aiuto ad altri utenti',
      icon: Users,
      link: '#',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      title: 'Blog',
      description: 'Scopri tips, tricks e best practices nel nostro blog',
      icon: Globe,
      link: '#',
      color: 'from-green-500 to-emerald-600'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-whatsapp-green to-emerald-600 rounded-full mb-4"
            >
              <HelpCircle className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Centro Assistenza
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-2xl mx-auto">
              Trova risposte alle tue domande e scopri come sfruttare al meglio WhatsApp Flow
            </p>
          </div>

          {/* Search Bar */}
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca nelle domande frequenti..."
                className="w-full pl-12 pr-4 py-3 bg-gray-100 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-xl focus:outline-none focus:ring-2 focus:ring-whatsapp-green/50 transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Categories */}
        <div className="flex flex-wrap gap-3 mb-8 justify-center">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-whatsapp-green to-emerald-600 text-white shadow-lg'
                  : 'bg-white dark:bg-dark-surface text-gray-700 dark:text-gray-300 hover:shadow-md'
              }`}
            >
              <category.icon className="w-4 h-4" />
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        {/* FAQs */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Domande Frequenti
          </h2>
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white dark:bg-dark-surface rounded-xl shadow-md overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <h3 className="text-left font-semibold text-gray-900 dark:text-white">
                    {faq.question}
                  </h3>
                  {expandedFAQ === index ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    className="px-6 pb-4"
                  >
                    <p className="text-gray-600 dark:text-gray-400">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Resources */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Risorse Utili
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, index) => (
              <motion.a
                key={resource.title}
                href={resource.link}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-surface rounded-xl shadow-md p-6 hover:shadow-lg transition-all group"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${resource.color} flex items-center justify-center mb-4`}>
                  <resource.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {resource.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {resource.description}
                </p>
                <div className="flex items-center gap-1 text-whatsapp-green group-hover:gap-2 transition-all">
                  <span className="text-sm">Scopri di più</span>
                  <ExternalLink className="w-4 h-4" />
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-whatsapp-green to-emerald-600 rounded-2xl p-8 text-white text-center"
        >
          <h2 className="text-2xl font-bold mb-4">
            Non hai trovato quello che cercavi?
          </h2>
          <p className="mb-6 opacity-90">
            Il nostro team di supporto è qui per aiutarti
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all">
              <MessageCircle className="w-5 h-5" />
              <span>Chatta con noi</span>
            </button>
            <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all">
              <Mail className="w-5 h-5" />
              <span>Invia email</span>
            </button>
          </div>
        </motion.div>

        {/* Quick Tips */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Suggerimenti Rapidi
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <Smartphone className="w-5 h-5 text-blue-600 dark:text-blue-400 mb-2" />
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Tip:</strong> Usa le scorciatoie da tastiera per lavorare più velocemente. Premi "?" per vederle tutte.
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <Zap className="w-5 h-5 text-green-600 dark:text-green-400 mb-2" />
              <p className="text-sm text-green-800 dark:text-green-300">
                <strong>Tip:</strong> Testa sempre le tue automazioni prima di attivarle per assicurarti che funzionino correttamente.
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400 mb-2" />
              <p className="text-sm text-purple-800 dark:text-purple-300">
                <strong>Tip:</strong> Fai regolarmente backup delle tue automazioni usando la funzione di esportazione.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
